// Blend view /b/:blendSlug (§5.3). Members load via get_blend (membership-
// checked, interactive). Anyone else — logged out or not a member — gets a
// read-only public view via get_blend_public (the slug is unguessable). Renders
// the shared BlendView plus the picks slot.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Page, Skeleton } from "../components/Layout.tsx";
import { BlendView } from "../components/BlendView.tsx";
import { PicksPanel } from "../components/PicksPanel.tsx";
import { useAuth } from "../auth/AuthProvider.tsx";
import { getBlend, getBlendPublic, generateBlendSummary, type BlendRow } from "../lib/api.ts";

export default function BlendScreen() {
  const { blendSlug = "" } = useParams();
  const auth = useAuth();
  const [blend, setBlend] = useState<BlendRow | null>(null);
  const [isMember, setIsMember] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [state, setState] = useState<"loading" | "ready" | "notfound">("loading");

  useEffect(() => {
    if (auth.loading) return;
    (async () => {
      // Members get the full interactive view; fall back to the public read.
      if (auth.user) {
        const b = await getBlend(blendSlug).catch(() => null);
        if (b) {
          setBlend(b);
          setIsMember(true);
          setSummary(b.summary ?? null);
          setState("ready");
          return;
        }
      }
      const pub = await getBlendPublic(blendSlug).catch(() => null);
      if (pub) {
        setBlend(pub);
        setIsMember(false);
        setSummary(pub.summary ?? null);
        setState("ready");
      } else {
        setState("notfound");
      }
    })();
  }, [auth.loading, auth.user, blendSlug]);

  // Members: generate the combined-taste blurb in the background when it's
  // missing (the server also regenerates past its TTL). Show the shimmer only
  // when there's nothing to display yet. Public viewers just read the cache.
  useEffect(() => {
    if (state !== "ready" || !isMember || summary) return;
    let cancelled = false;
    setSummaryLoading(true);
    generateBlendSummary(blendSlug)
      .then((r) => {
        if (!cancelled && r.summary) setSummary(r.summary);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [state, isMember, summary, blendSlug]);

  // "Don't like it? Get another" — force a fresh blurb.
  async function regenerateSummary() {
    setSummaryLoading(true);
    try {
      const r = await generateBlendSummary(blendSlug, true);
      if (r.summary) setSummary(r.summary);
    } catch {
      /* keep the old one */
    } finally {
      setSummaryLoading(false);
    }
  }

  if (state === "loading") {
    return (
      <Page testid="screen-blend" wide bare>
        <Skeleton className="h-32 w-full" />
        <div className="mt-6 flex gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 w-28" />
          ))}
        </div>
      </Page>
    );
  }

  if (state === "notfound" || !blend) {
    return (
      <Page testid="screen-blend">
        <div className="card text-center">
          <h1 className="text-2xl font-semibold text-ink-900">Blend not found</h1>
          <p className="mt-2 text-ink-400">It may have been deleted, or you're not part of it.</p>
          <a href="/me" className="btn-primary mt-4">Back to dashboard</a>
        </div>
      </Page>
    );
  }

  const a = blend.profile_a;
  const b = blend.profile_b;

  // Watched-title sets for filtering picks: `either` (union) blocks new-ground
  // suggestions anyone has seen; `both` (intersection) blocks bridge/referee
  // suggestions they've both already seen.
  const key = (t: string, y?: number) => `${t.trim().toLowerCase()}|${y ?? ""}`;
  const watchedA = new Set<string>();
  const watchedB = new Set<string>();
  for (const f of Object.values(a.profile_data.films)) if (f.s.includes("w")) watchedA.add(key(f.t, f.y));
  for (const f of Object.values(b.profile_data.films)) if (f.s.includes("w")) watchedB.add(key(f.t, f.y));
  const either = new Set<string>([...watchedA, ...watchedB]);
  const both = new Set<string>([...watchedA].filter((k) => watchedB.has(k)));

  return (
    <Page testid="screen-blend" wide bare>
      <BlendView
        profileA={a.profile_data}
        profileB={b.profile_data}
        nameA={a.display_name}
        nameB={b.display_name}
        blendSlug={blend.blend_slug}
        summary={summary}
        summaryLoading={summaryLoading}
        onRegenerateSummary={isMember ? regenerateSummary : undefined}
        showLucky={isMember}
        picksSlot={<PicksPanel blendSlug={blend.blend_slug} recs={blend.recs} seen={{ either, both }} readOnly={!isMember} />}
      />
    </Page>
  );
}
